import { GeoReference, latLonToRoiPx, roiPxToLatLon } from './coordinateConversion'
import { probeGreenTouchingCircles, withinPct } from './dataUtils'
import { angleDeg, isInwardByCenter, neighborNames, runOneDirWithAdaptive as runOneDirAdaptiveHelper } from './geometry'
import {
  buildEdgeParams,
  DEFAULT_GREEN_AUTO_MAP_PARAMS,
  EdgeParams,
  GREEN_AUTO_MAP_MIN_PIX,
  GREEN_AUTO_MAP_PROBE_MIN_PIX,
  GREEN_AUTO_MAP_SAMPLING_RADIUS_M,
  getInitialEdgeParams,
  LEVELS,
  ParamLevel,
  moveLevel,
  selectEdgeParamsFromProbe,
} from './parameterSelection'
import { retryRunnerLengthSpike, ranToEndWithoutStop, rayLenPx, RayWalkResult, RayStep } from './rayWalking'

function logImagePreviewUrl(url: string, label: string, width: number, height: number): void {
  if (typeof Image === 'undefined') {
    return
  }

  const maxPreviewSizePx = 960
  const scale = Math.min(1, maxPreviewSizePx / Math.max(width, height))
  const image = new Image()

  image.onload = () => {
    const previewWidth = Math.max(1, Math.round(image.width * scale))
    const previewHeight = Math.max(1, Math.round(image.height * scale))
    const style = [
      'font-size: 1px',
      `padding: ${Math.max(1, Math.round(previewHeight / 2))}px ${Math.max(1, Math.round(previewWidth / 2))}px`,
      `background: url(${url}) no-repeat`,
      'background-size: contain',
      'color: transparent',
    ].join('; ')

    console.log(label)
    console.log('%c ', style)
  }

  image.src = url
}

function logGreenAutoMapOverlayPreview({
  roiRgb,
  centerPx,
  dirs,
  results,
  probeResult,
  radiusPx,
  chosenPreset,
}: {
  roiRgb: ImageData
  centerPx: [number, number]
  dirs: Record<string, [number, number]>
  results: Record<string, RayWalkResult | null>
  probeResult: {
    probeRadiusM: number
    offsetM: number
    keptKeys: string[]
    summaries: Record<string, unknown | null>
    mpp: number
  } | null
  radiusPx: number
  chosenPreset: string
}): void {
  if (typeof document === 'undefined') {
    return
  }

  const canvas = document.createElement('canvas')
  canvas.width = roiRgb.width
  canvas.height = roiRgb.height
  const context = canvas.getContext('2d')

  if (!context) {
    return
  }

  context.putImageData(roiRgb, 0, 0)
  context.lineCap = 'round'
  context.lineJoin = 'round'
  context.font = '12px monospace'
  context.textAlign = 'center'
  context.textBaseline = 'middle'

  if (probeResult) {
    const keptKeys = new Set(probeResult.keptKeys)
    const probeRadiusPx = probeResult.probeRadiusM / probeResult.mpp
    const probeOffsetPx = probeResult.offsetM / probeResult.mpp
    const [cx, cy] = centerPx
    const probeCenters: Record<string, [number, number]> = {
      C: [cx, cy],
      N: [cx, cy - probeOffsetPx],
      E: [cx + probeOffsetPx, cy],
      S: [cx, cy + probeOffsetPx],
      W: [cx - probeOffsetPx, cy],
    }

    for (const [key, [px, py]] of Object.entries(probeCenters)) {
      const summary = probeResult.summaries[key]
      const strokeStyle = key === 'C' || (summary && keptKeys.has(key)) ? '#ffffff' : 'rgba(255, 255, 255, 0.55)'

      context.beginPath()
      context.strokeStyle = strokeStyle
      context.lineWidth = 2
      context.arc(px, py, probeRadiusPx, 0, Math.PI * 2)
      context.stroke()
    }
  }

  for (const [name, dirVec] of Object.entries(dirs)) {
    const result = results[name]
    const fallbackEnd: [number, number] = [
      Math.round(centerPx[0] + dirVec[0] * radiusPx),
      Math.round(centerPx[1] + dirVec[1] * radiusPx),
    ]
    const endPoint = result?.lastGood?.centerPx ?? result?.firstBad?.centerPx ?? fallbackEnd
    const rayColor = !result
      ? 'rgba(255, 99, 99, 0.8)'
      : result.ranToEnd
        ? 'rgba(255, 184, 77, 0.9)'
        : result.lastGood
          ? 'rgba(80, 220, 255, 0.85)'
          : 'rgba(255, 99, 99, 0.8)'

    if (result?.rows?.length) {
      context.fillStyle = rayColor
      for (const step of result.rows) {
        context.beginPath()
        context.arc(step.centerPx[0], step.centerPx[1], 1.6, 0, Math.PI * 2)
        context.fill()
      }
    }

    const rayLabelDistancePx = 20
    const rayLength = Math.hypot(dirVec[0], dirVec[1]) || 1
    const labelX = endPoint[0] + (dirVec[0] / rayLength) * rayLabelDistancePx
    const labelY = endPoint[1] + (dirVec[1] / rayLength) * rayLabelDistancePx

    context.lineWidth = 3
    context.strokeStyle = 'rgba(0, 0, 0, 0.8)'
    context.strokeText(name, labelX, labelY)
    context.fillStyle = '#ffffff'
    context.fillText(name, labelX, labelY)

    if (result?.lastGood) {
      context.beginPath()
      context.fillStyle = '#ffffff'
      context.arc(result.lastGood.centerPx[0], result.lastGood.centerPx[1], 2.5, 0, Math.PI * 2)
      context.fill()
    }

    if (result?.firstBad) {
      context.beginPath()
      context.strokeStyle = '#ff6b6b'
      context.lineWidth = 1.5
      context.arc(result.firstBad.centerPx[0], result.firstBad.centerPx[1], 3.5, 0, Math.PI * 2)
      context.stroke()
    }
  }

  context.beginPath()
  context.fillStyle = '#ffffff'
  context.arc(centerPx[0], centerPx[1], 4, 0, Math.PI * 2)
  context.fill()

  logImagePreviewUrl(
    canvas.toDataURL('image/png'),
    `[green-auto-map] probe + rays (${Object.keys(dirs).length} rays, preset ${chosenPreset})`,
    canvas.width,
    canvas.height,
  )
}

function logGreenAutoMapScaleInfo({
  geoRef,
  roiRgb,
  centerPx,
  probeRadiusM,
  patchHalfPx,
  maxSearchRadiusM,
  radiusPx,
  sampleResult,
}: {
  geoRef: GeoReference
  roiRgb: ImageData
  centerPx: [number, number]
  probeRadiusM: number
  patchHalfPx: number
  maxSearchRadiusM: number
  radiusPx: number
  sampleResult: RayWalkResult | null
}): void {
  const probeRadiusPx = probeRadiusM / geoRef.mpp
  const dStartPx = sampleResult?.dStart ?? null
  const stepPx = sampleResult?.step ?? null
  const dMaxPx = sampleResult?.dMax ?? null

  console.groupCollapsed('[green-auto-map] Scale info')
  console.log({
    zoom: geoRef.zoom,
    mpp: Number(geoRef.mpp.toFixed(6)),
    'ROI image width x height': `${roiRgb.width} x ${roiRgb.height}`,
    'center pixel (ax, ay)': centerPx,
    probeRadiusM: Number(probeRadiusM.toFixed(3)),
    probeRadiusPx: Number(probeRadiusPx.toFixed(3)),
    patchHalfPx,
    patchHalfMeters: Number((patchHalfPx * geoRef.mpp).toFixed(6)),
    maxSearchRadiusM: Number(maxSearchRadiusM.toFixed(3)),
    radiusPx,
    dStartPx,
    dStartM: dStartPx === null ? null : Number((dStartPx * geoRef.mpp).toFixed(6)),
    stepPx,
    stepMeters: stepPx === null ? null : Number((stepPx * geoRef.mpp).toFixed(6)),
    dMaxPx,
    dMaxM: dMaxPx === null ? null : Number((dMaxPx * geoRef.mpp).toFixed(6)),
  })
  console.groupEnd()
}

function formatRayStepSnapshot(
  step: RayStep | null,
  previousStep: RayStep | null,
  baseLab: [number, number, number] | null,
  mpp: number,
) {
  if (!step) {
    return null
  }

  const [, baseA, baseB] = baseLab ?? [null, null, null]
  const [medL, medA, medB] = step.medLab
  const daStep = previousStep ? medA - previousStep.medLab[1] : 0
  const dbStep = previousStep ? medB - previousStep.medLab[2] : 0
  const daBase = baseA === null ? 0 : medA - baseA
  const dbBase = baseB === null ? 0 : medB - baseB

  return {
    step: step.step,
    distancePx: step.d,
    distanceM: Number((step.d * mpp).toFixed(6)),
    pointPx: step.centerPx,
    medLab: [Number(medL.toFixed(2)), Number(medA.toFixed(2)), Number(medB.toFixed(2))],
    medL: Number(medL.toFixed(2)),
    medA: Number(medA.toFixed(2)),
    medB: Number(medB.toFixed(2)),
    deMed: Number(step.deMed.toFixed(2)),
    ratioAb: Number(step.ratioAb.toFixed(3)),
    minRatioAb: Number(step.minRatioAbUsed.toFixed(3)),
    medShiftAb: Number(step.shiftAb.toFixed(2)),
    lDiff: Number(step.lDiff.toFixed(2)),
    lStep: Number(step.lStep.toFixed(2)),
    daStep: Number(daStep.toFixed(2)),
    dbStep: Number(dbStep.toFixed(2)),
    daBase: Number(daBase.toFixed(2)),
    dbBase: Number(dbBase.toFixed(2)),
    absDeltaAbStep: Number((Math.abs(daStep) + Math.abs(dbStep)).toFixed(2)),
    absDeltaAbBase: Number((Math.abs(daBase) + Math.abs(dbBase)).toFixed(2)),
    stopReasons: step.stopReasons,
    forcedOk: step.forcedOk,
  }
}

function formatRayStepLogLine(
  rayName: string,
  step: RayStep,
  previousStep: RayStep | null,
  baseLab: [number, number, number] | null,
): string {
  const signed = (value: number, decimals: number) => `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}`

  const [, baseA, baseB] = baseLab ?? [null, null, null]
  const [, medA, medB] = step.medLab
  const daStep = previousStep ? medA - previousStep.medLab[1] : 0
  const dbStep = previousStep ? medB - previousStep.medLab[2] : 0
  const daBase = baseA === null ? 0 : medA - baseA
  const dbBase = baseB === null ? 0 : medB - baseB
  const absAbStep = Math.hypot(daStep, dbStep)
  const absAbBase = Math.hypot(daBase, dbBase)

  return [
    `[ray ${rayName}]`,
    `step=${String(step.step).padStart(2, '0')}`,
    `ratio_ab=${step.ratioAb.toFixed(2)}`,
    `shift_ab=${step.shiftAb.toFixed(1)}`,
    `de_med=${step.deMed.toFixed(1)}`,
    `L_diff=${step.lDiff.toFixed(1)}`,
    `L_step=${step.lStep.toFixed(1)}`,
    `da_step=${signed(daStep, 1)}`,
    `db_step=${signed(dbStep, 1)}`,
    `|Δab_step|=${absAbStep.toFixed(1)}`,
    `da_base=${signed(daBase, 1)}`,
    `db_base=${signed(dbBase, 1)}`,
    `|Δab_base|=${absAbBase.toFixed(1)}`,
  ].join(' ')
}

function logGreenAutoMapStepDetails({
  results,
  chosenPreset,
}: {
  results: Record<string, RayWalkResult | null>
  chosenPreset: string
}): void {
  const rayEntries = Object.entries(results).sort(([left], [right]) => Number(left) - Number(right))
  console.log(`[green-auto-map] step details (${rayEntries.length} rays, preset ${chosenPreset})`)
  for (const [name, result] of rayEntries) {
    if (!result || result.rows.length === 0) {
      console.log(`[ray ${name}] no steps`)
      continue
    }
    const rayLines = result.rows.map((step, index) =>
      formatRayStepLogLine(name, step, index > 0 ? result.rows[index - 1] : null, result.baseLab ?? null),
    )
    console.groupCollapsed(`[ray ${name}] (${result.rows.length} steps)`)
    console.log(rayLines.join('\n'))
    console.groupEnd()
  }
}

function logGreenAutoMapStopDiagnostics({
  results,
  chosenPreset,
  baseLevels,
  edgeParams,
  mpp,
  probeSelectionDebug,
  runnerFixLog,
  lengthSpikeFixLog,
  inwardFixLog,
}: {
  results: Record<string, RayWalkResult | null>
  chosenPreset: string
  baseLevels: { L: ParamLevel; AB: ParamLevel }
  edgeParams: EdgeParams
  mpp: number
  probeSelectionDebug: {
    stdAB: number
    stdL: number
    maxAbsAB: number
  } | null
  runnerFixLog: Record<string, unknown[]>
  lengthSpikeFixLog: Record<string, unknown[]>
  inwardFixLog: Record<string, { history: unknown[] }>
}): void {
  const raySummaries = Object.entries(results)
    .sort(([left], [right]) => Number(left) - Number(right))
    .map(([name, result]) => {
      const firstBad = result?.firstBad ?? null
      const lastGood = result?.lastGood ?? null
      const rows = result?.rows ?? []
      const lastGoodIndex = rows.findIndex((row) => row === lastGood)
      const firstBadIndex = rows.findIndex((row) => row === firstBad)
      const getPreviousStep = (index: number) => (index > 0 ? rows[index - 1] : null)
      const recentSteps = rows
        .slice(-3)
        .map((step, index, recentRows) =>
          formatRayStepSnapshot(step, index > 0 ? recentRows[index - 1] : null, result?.baseLab ?? null, mpp),
        )
      const endState = !result ? 'no_result' : result.ranToEnd ? 'ran_to_end' : firstBad ? 'stopped' : 'unknown'
      const stopReasons = firstBad?.stopReasons ?? (result?.ranToEnd ? ['ran_to_end'] : ['no_stop_reason'])

      return {
        ray: name,
        endState,
        stopReasons,
        lastGood: formatRayStepSnapshot(lastGood, getPreviousStep(lastGoodIndex), result?.baseLab ?? null, mpp),
        firstBad: formatRayStepSnapshot(firstBad, getPreviousStep(firstBadIndex), result?.baseLab ?? null, mpp),
        recentSteps,
        runnerFixes: runnerFixLog[name] ?? [],
        lengthSpikeFixes: lengthSpikeFixLog[name] ?? [],
        inwardFixes: inwardFixLog[name]?.history ?? [],
      }
    })

  console.groupCollapsed(`[green-auto-map] stop diagnostics (${raySummaries.length} rays, preset ${chosenPreset})`)
  console.log('resolved levels + params', {
    chosenPreset,
    baseLevels,
    edgeParams,
    probeParams: probeSelectionDebug
      ? {
          std_ab: Number(probeSelectionDebug.stdAB.toFixed(6)),
          std_L: Number(probeSelectionDebug.stdL.toFixed(6)),
          max_abs_ab: Number(probeSelectionDebug.maxAbsAB.toFixed(6)),
        }
      : null,
  })
  console.table(
    raySummaries.map((summary) => ({
      ray: summary.ray,
      endState: summary.endState,
      stopReason: summary.stopReasons.join(' | '),
      lastGoodStep: summary.lastGood?.step ?? null,
      lastGoodDistancePx: summary.lastGood?.distancePx ?? null,
      lastGoodDistanceM: summary.lastGood?.distanceM ?? null,
      firstBadStep: summary.firstBad?.step ?? null,
      firstBadDistancePx: summary.firstBad?.distancePx ?? null,
      firstBadDistanceM: summary.firstBad?.distanceM ?? null,
      deMed: summary.firstBad?.deMed ?? null,
      ratioAb: summary.firstBad?.ratioAb ?? null,
      medShiftAb: summary.firstBad?.medShiftAb ?? null,
      lDiff: summary.firstBad?.lDiff ?? null,
      lStep: summary.firstBad?.lStep ?? null,
      runnerFixes: summary.runnerFixes.length,
      lengthSpikeFixes: summary.lengthSpikeFixes.length,
      inwardFixes: summary.inwardFixes.length,
    })),
  )
  console.log('ray details', raySummaries)
  console.groupEnd()
}

export const runEdgesAndPlot = async ({
  coordinates: { lat: gLat, lon: gLon },
  // roiMargin = 1.05,
  lPresetIndex = DEFAULT_GREEN_AUTO_MAP_PARAMS.lPresetIndex,
  abPresetIndex = DEFAULT_GREEN_AUTO_MAP_PARAMS.abPresetIndex,
  rayCount = DEFAULT_GREEN_AUTO_MAP_PARAMS.rayCount,
  loosenessBias = DEFAULT_GREEN_AUTO_MAP_PARAMS.loosenessBias,
  correctRayOutliers = DEFAULT_GREEN_AUTO_MAP_PARAMS.correctRayOutliers,
  debugStopLogging = DEFAULT_GREEN_AUTO_MAP_PARAMS.debugStopLogging,
  debugStepLogging = DEFAULT_GREEN_AUTO_MAP_PARAMS.debugStepLogging,
  debugImageLogging = DEFAULT_GREEN_AUTO_MAP_PARAMS.debugImageLogging,
  abGate = DEFAULT_GREEN_AUTO_MAP_PARAMS.abGate,
  patchHalfPx = DEFAULT_GREEN_AUTO_MAP_PARAMS.patchHalfPx,
  stepPx = DEFAULT_GREEN_AUTO_MAP_PARAMS.stepPx,
  probeRadiusM = DEFAULT_GREEN_AUTO_MAP_PARAMS.probeRadiusM,
  // FIXED: Increased from 30m to match Python implementation
  // Python uses hole_length/6 which is typically 30-50m for golf greens
  // This allows rays to travel further, creating larger mapped area
  maxSearchRadiusM = DEFAULT_GREEN_AUTO_MAP_PARAMS.maxSearchRadiusM,
  roiRgb,
  geoRef,
}: {
  coordinates: { lat: number; lon: number }
  roiMargin?: number
  lPresetIndex?: number
  abPresetIndex?: number
  rayCount?: number
  loosenessBias?: number
  correctRayOutliers?: boolean
  debugStopLogging?: boolean
  debugStepLogging?: boolean
  debugImageLogging?: boolean
  abGate?: number
  patchHalfPx?: number
  stepPx?: number
  probeRadiusM?: number
  maxSearchRadiusM?: number
  roiRgb: ImageData
  geoRef: GeoReference
}) => {
  // // 1) Resolve hole + ROI geometry
  // const { g_lat, g_lon, roi_radius_m } = holeSpecs[holeId]
  // const half_size_m = roi_radius_m * config.roi_margin
  // const zoom = chooseZoomForRadius(half_size_m, config.target_radius_px)

  // // 2) Fetch ROI image
  // const { roi_rgb, georef } = await fetchRoiSatelliteWithFallback(g_lat, g_lon, zoom, holeId)
  // const { mpp, x0g, y0g, H, W } = georef
  // const valid_mask_u8 = createValidMask(roi_rgb)

  const validMask = new Uint8Array(roiRgb.width * roiRgb.height).fill(255)
  const totalDirs = Math.max(3, Math.round(rayCount))

  const [axPx, ayPx] = latLonToRoiPx(gLat, gLon, geoRef)
  const axPxRounded = Math.round(axPx)
  const ayPxRounded = Math.round(ayPx)

  // Calculate radius in pixels from desired search distance in meters
  const radiusPx = Math.round(maxSearchRadiusM / geoRef.mpp)
  const samplingRadiusPx = Math.max(1, Math.round(GREEN_AUTO_MAP_SAMPLING_RADIUS_M / geoRef.mpp))
  const patchHalfPxNormalized = Math.max(1, Math.round(patchHalfPx))
  const stepPxNormalized = Math.max(1, Math.round(stepPx))

  // 3) Probe green color at center (and 4 touching circles)
  const probeResult = probeGreenTouchingCircles(
    roiRgb,
    geoRef.W,
    geoRef.H,
    validMask,
    [axPxRounded, ayPxRounded],
    geoRef,
    probeRadiusM,
    GREEN_AUTO_MAP_PROBE_MIN_PIX,
  )

  let edgeParams = getInitialEdgeParams()

  let baseLLevel: ParamLevel = 'loose'
  let baseABLevel: ParamLevel = 'loose'
  let chosenPreset = 'fallback'
  let probeSelectionDebug: {
    stdAB: number
    stdL: number
    maxAbsAB: number
  } | null = null
  let extraLooseLSteps = 0
  let extraLooseABSteps = 0

  const resolveParamLevelFromIndex = (value: number): { level: ParamLevel | null; extraLooseSteps: number } => {
    if (!Number.isInteger(value) || value < 0) {
      return {
        level: null,
        extraLooseSteps: 0,
      }
    }

    const maxLevelIndex = LEVELS.length - 1
    const levelIndex = Math.min(value, maxLevelIndex)

    return {
      level: LEVELS[levelIndex],
      extraLooseSteps: Math.max(0, value - maxLevelIndex),
    }
  }

  const applyLevelBias = (level: ParamLevel, bias: number): { level: ParamLevel; stepsApplied: number } => {
    let nextLevel = level
    const direction = bias >= 0 ? 'looser' : 'stricter'
    let stepsApplied = 0

    for (let stepIndex = 0; stepIndex < Math.abs(bias); stepIndex++) {
      const candidate = moveLevel(nextLevel, direction)
      if (!candidate) {
        break
      }

      nextLevel = candidate
      stepsApplied += 1
    }

    return {
      level: nextLevel,
      stepsApplied,
    }
  }

  const buildAdjustedEdgeParams = (lLevel: ParamLevel, abLevel: ParamLevel): EdgeParams => {
    const params = buildEdgeParams(lLevel, abLevel)

    if (extraLooseLSteps === 0 && extraLooseABSteps === 0) {
      return params
    }

    return {
      ...params,
      abGate,
      deMedStop: params.deMedStop + extraLooseLSteps * 2.0,
      lAbsStop: params.lAbsStop + extraLooseLSteps * 4.0,
      lStepStop: params.lStepStop + extraLooseLSteps * 2.0,
      minRatioAb: Math.max(0.02, params.minRatioAb - extraLooseABSteps * 0.08),
      maxMedShiftAb: params.maxMedShiftAb + extraLooseABSteps * 1.5,
      abStepStop: params.abStepStop + extraLooseABSteps * 2.0,
      abBaseStop: params.abBaseStop + extraLooseABSteps * 1.5,
      abStepSumStop: params.abStepSumStop + extraLooseABSteps * 2.0,
      abBaseSumStop: params.abBaseSumStop + extraLooseABSteps * 2.5,
    }
  }

  edgeParams = {
    ...edgeParams,
    abGate,
  }

  if (!probeResult) {
    chosenPreset = 'tight'
    baseLLevel = 'tight'
    baseABLevel = 'tight'
    edgeParams = buildAdjustedEdgeParams(baseLLevel, baseABLevel)
  } else if (probeResult) {
    const [, edgeParamsFromProbe, debug] = selectEdgeParamsFromProbe(probeResult)
    edgeParams = edgeParamsFromProbe
    chosenPreset = `L:${debug.lLevel} | AB:${debug.abLevel}`
    baseLLevel = debug.lLevel
    baseABLevel = debug.abLevel
    probeSelectionDebug = {
      stdAB: debug.stdAB,
      stdL: debug.stdL,
      maxAbsAB: debug.maxAbsAB,
    }
  }

  const manualLSelection = resolveParamLevelFromIndex(lPresetIndex)
  const manualABSelection = resolveParamLevelFromIndex(abPresetIndex)
  const manualLLevel = manualLSelection.level
  const manualABLevel = manualABSelection.level

  if (manualLLevel || manualABLevel) {
    const originalLLevel = baseLLevel
    const originalABLevel = baseABLevel

    if (manualLLevel) {
      baseLLevel = manualLLevel
    }
    if (manualABLevel) {
      baseABLevel = manualABLevel
    }

    extraLooseLSteps = manualLSelection.extraLooseSteps
    extraLooseABSteps = manualABSelection.extraLooseSteps

    edgeParams = buildAdjustedEdgeParams(baseLLevel, baseABLevel)
    const extrasLabel =
      extraLooseLSteps > 0 || extraLooseABSteps > 0 ? ` + extra(L:${extraLooseLSteps},AB:${extraLooseABSteps})` : ''
    chosenPreset = `L:${originalLLevel}->${baseLLevel} | AB:${originalABLevel}->${baseABLevel}${extrasLabel}`
  }

  if (!manualLLevel && !manualABLevel && loosenessBias !== 0) {
    const originalLLevel = baseLLevel
    const originalABLevel = baseABLevel
    const adjustedL = applyLevelBias(baseLLevel, loosenessBias)
    const adjustedAB = applyLevelBias(baseABLevel, loosenessBias)

    baseLLevel = adjustedL.level
    baseABLevel = adjustedAB.level
    extraLooseLSteps = loosenessBias > 0 ? Math.max(0, loosenessBias - adjustedL.stepsApplied) : 0
    extraLooseABSteps = loosenessBias > 0 ? Math.max(0, loosenessBias - adjustedAB.stepsApplied) : 0
    edgeParams = buildAdjustedEdgeParams(baseLLevel, baseABLevel)

    const extrasLabel =
      extraLooseLSteps > 0 || extraLooseABSteps > 0 ? ` + extra(L:${extraLooseLSteps},AB:${extraLooseABSteps})` : ''
    chosenPreset = `L:${originalLLevel}->${baseLLevel} | AB:${originalABLevel}->${baseABLevel}${extrasLabel}`
  }

  // 4) Define run_one_dir_with_adaptive(...)
  const runOneDirWithAdaptive = (name: string, dirVec: [number, number], params: EdgeParams = edgeParams) => {
    // Walks outward from center with adaptive retry (scale 1.0, 0.5, 0.25)
    return runOneDirAdaptiveHelper(
      name,
      dirVec,
      params,
      axPxRounded,
      ayPxRounded,
      radiusPx,
      samplingRadiusPx,
      patchHalfPxNormalized,
      stepPxNormalized,
      roiRgb,
      geoRef.W,
      geoRef.H,
      validMask,
      GREEN_AUTO_MAP_MIN_PIX,
      geoRef.mpp,
    )
  }

  // 5) Pass 1 — baseline rays
  const dirs: Record<string, [number, number]> = {}
  for (let i = 0; i < totalDirs; i++) {
    const theta = (i * 2 * Math.PI) / totalDirs
    dirs[String(i + 1)] = [Math.cos(theta), Math.sin(theta)]
  }

  const getNeighborNames = (name: string): [string, string] => neighborNames(name, totalDirs)

  const results: Record<string, RayWalkResult | null> = {}
  const edgePoints: Record<string, [number, number]> = {}
  for (const [name, v] of Object.entries(dirs)) {
    const res = runOneDirWithAdaptive(name, v)
    results[name] = res

    if (res?.lastGood) {
      edgePoints[name] = res.lastGood.centerPx
    }
  }

  const ANGLE_CANDIDATE = 150.0
  const INWARD_MARGIN_PX = 2.0

  // Logging for passes
  const runnerFixLog: Record<string, any[]> = {}
  const lengthSpikeFixLog: Record<string, any[]> = {}
  const centerXy: [number, number] = [axPxRounded, ayPxRounded]

  function medianLenNonRunners(): number | null {
    const lens: number[] = []

    for (let i = 1; i <= totalDirs; i++) {
      const name = String(i)
      if (ranToEndWithoutStop(results[name])) {
        continue
      }

      const len = rayLenPx(edgePoints, centerXy, name)
      if (len !== null) {
        lens.push(len)
      }
    }

    if (lens.length === 0) {
      return null
    }

    lens.sort((a, b) => a - b)
    const mid = Math.floor(lens.length / 2)

    return lens.length % 2 === 1 ? lens[mid] : (lens[mid - 1] + lens[mid]) / 2
  }

  function isAbnormallyShortGlobal(name: string, frac: number, medianLen: number | null): boolean {
    const len = rayLenPx(edgePoints, centerXy, name)

    if (len === null || medianLen === null || medianLen <= 1e-6) {
      return false
    }

    return len < frac * medianLen
  }

  // Helper to check if should retry a runner (ray that ran to end without stopping)
  function shouldRetryRunner(name: string): boolean {
    const res = results[name]
    if (!res || !ranToEndWithoutStop(res)) {
      return false
    }
    // Check if neighbors exist
    const [left, right] = getNeighborNames(name)
    return left in edgePoints && right in edgePoints
  }

  // Helper to check if should retry a length spike
  function shouldRetryLengthSpike(name: string): boolean {
    const res = results[name]
    // Must not be a runner
    if (!res || ranToEndWithoutStop(res)) {
      return false
    }
    // Must have neighbors
    const [left, right] = getNeighborNames(name)
    if (!(left in edgePoints) || !(name in edgePoints) || !(right in edgePoints)) {
      return false
    }

    const ang = angleDeg(edgePoints[left], edgePoints[name], edgePoints[right])
    if (ang === null || ang >= 70) {
      return false
    }

    const isInward = isInwardByCenter(
      axPxRounded,
      ayPxRounded,
      edgePoints[left],
      edgePoints[name],
      edgePoints[right],
      INWARD_MARGIN_PX,
    )
    if (isInward) {
      return false
    }

    const lenMid = Math.hypot(edgePoints[name][0] - axPxRounded, edgePoints[name][1] - ayPxRounded)
    const lenLeft = Math.hypot(edgePoints[left][0] - axPxRounded, edgePoints[left][1] - ayPxRounded)
    const lenRight = Math.hypot(edgePoints[right][0] - axPxRounded, edgePoints[right][1] - ayPxRounded)
    const avgNeighbor = (lenLeft + lenRight) / 2

    const exceedsAverage = lenMid > avgNeighbor * 1.3
    const exceedsEachNeighbor = lenMid > lenLeft * 1.3 && lenMid > lenRight * 1.3

    return exceedsAverage && exceedsEachNeighbor
  }

  // ======== PASS 2: Fix runners (rays that ran to end) ========
  for (const [name, v] of Object.entries(dirs)) {
    if (!correctRayOutliers) {
      break
    }
    if (!shouldRetryRunner(name)) {
      continue
    }

    // Try progressively stricter parameters
    let curLLevel: ParamLevel = baseLLevel
    let curABLevel: ParamLevel = baseABLevel
    let bestRes = results[name]
    let bestPt = edgePoints[name]
    let improved = false

    for (let attempt = 0; attempt < 6; attempt++) {
      const nextL = moveLevel(curLLevel, 'stricter')
      const nextAB = moveLevel(curABLevel, 'stricter')

      if (!nextL && !nextAB) {
        break
      }

      if (nextL) {
        curLLevel = nextL
      }
      if (nextAB) {
        curABLevel = nextAB
      }

      const stricterParams = buildAdjustedEdgeParams(curLLevel, curABLevel)
      const resTry = runOneDirWithAdaptive(name, v, stricterParams)

      if (resTry?.lastGood) {
        const [left, right] = getNeighborNames(name)
        const ang = angleDeg(edgePoints[left], resTry.lastGood.centerPx, edgePoints[right])

        // Accept if it stopped (didn't run to end) and has a better angle
        if (!ranToEndWithoutStop(resTry)) {
          improved = true
          bestRes = resTry
          bestPt = resTry.lastGood.centerPx

          // Stop early if angle is good (>= 150°)
          if (ang !== null && ang >= ANGLE_CANDIDATE) {
            break
          }
        }
      }
    }

    if (improved && bestRes && bestPt) {
      results[name] = bestRes
      edgePoints[name] = bestPt
      runnerFixLog[name] = runnerFixLog[name] || []
      runnerFixLog[name].push({ pass: 2, improved: true, levels: { L: curLLevel, AB: curABLevel } })
    }
  }

  // ======== PASS 3: Fix length spikes ========

  for (const [name, v] of Object.entries(dirs)) {
    if (!correctRayOutliers) {
      break
    }
    if (!shouldRetryLengthSpike(name)) {
      continue
    }

    const result = retryRunnerLengthSpike(name, v, {
      results,
      edgePoints,
      baseLLevel,
      baseABLevel,
      axPx: axPxRounded,
      ayPx: ayPxRounded,
      runOneDirWithAdaptive: (n: string, vec: [number, number], params: EdgeParams) =>
        runOneDirAdaptiveHelper(
          n,
          vec,
          params,
          axPxRounded,
          ayPxRounded,
          radiusPx,
          samplingRadiusPx,
          patchHalfPxNormalized,
          stepPxNormalized,
          roiRgb,
          geoRef.W,
          geoRef.H,
          validMask,
          GREEN_AUTO_MAP_MIN_PIX,
          geoRef.mpp,
        ),
      buildEdgeParams: buildAdjustedEdgeParams,
      getNeighborNames,
      gateDebug: 'spike',
      maxTries: 6,
    })

    if (result.improved && result.bestRes && result.bestPt) {
      results[name] = result.bestRes
      edgePoints[name] = result.bestPt
      lengthSpikeFixLog[name] = lengthSpikeFixLog[name] || []
      lengthSpikeFixLog[name].push({
        pass: 3,
        improved: true,
        debug: result.debug,
      })
    }
  }
  // ======== PASS 4: Fix inward dents ========

  // Pass 4 specific constants (ANGLE_CANDIDATE and INWARD_MARGIN_PX already defined above)
  const ANGLE_OUTWARD_GOOD_MIN = 90.0
  const ANGLE_OUTWARD_OK_MIN = 60.0
  const ANGLE_SHARP_OUTWARD_MAX = 60.0
  const IMPROVE_MIN_DEG = 1.0
  const MAX_TRIES_PER_RAY = 12

  const inwardFixLog: Record<string, { history: unknown[] }> = {}

  // Helper: Classify stop reasons to determine if L-based, AB-based, or both
  const classifyStopLAB = (resTry: RayWalkResult | null): [boolean, boolean] => {
    if (!resTry || !resTry.rows) {
      return [true, true]
    }

    let stopRow: RayStep | null = null
    for (const r of resTry.rows) {
      if (!r.ok) {
        stopRow = r
        break
      }
    }
    if (!stopRow) {
      return [true, true]
    }

    const reasons = stopRow.stopReasons || []

    const lHit = reasons.some(
      (rr) => rr.includes('L_diff') || rr.includes('ΔL_step') || rr.includes('|L_diff|') || rr.includes('L_step'),
    )
    const abHit = reasons.some(
      (rr) =>
        rr.includes('ratio_ab') ||
        rr.includes('shift_ab') ||
        rr.includes('Δa_') ||
        rr.includes('Δb_') ||
        rr.includes('Δab_') ||
        rr.includes('ab_step') ||
        rr.includes('ab_base'),
    )

    if (reasons.some((rr) => rr.includes('de_med'))) {
      return [true, true]
    }

    if (!lHit && !abHit) {
      return [true, true]
    }

    return [lHit, abHit]
  }

  // Helper: Check if ray is a legit neighbor (stopped without running to end)
  const isLegitNeighbor = (rayName: string): boolean => {
    const res = results[rayName]
    return !!res && !!res.firstBad && !res.ranToEnd
  }

  // Helper: Current angle for a ray
  const currentAngle = (name: string): [number | null, string, string] => {
    const [left, right] = getNeighborNames(name)
    if (!edgePoints[left] || !edgePoints[name] || !edgePoints[right]) {
      return [null, left, right]
    }
    const ang = angleDeg(edgePoints[left], edgePoints[name], edgePoints[right])
    return [ang, left, right]
  }

  // Helper: Check if a ray is an inward dent candidate
  const isCandidate = (name: string): boolean => {
    const [left, right] = getNeighborNames(name)

    // 1) Need all three edge points
    if (!edgePoints[left] || !edgePoints[name] || !edgePoints[right]) {
      return false
    }

    // 2) Neighbors must be reliable (stopped, not runners)
    if (!isLegitNeighbor(left) || !isLegitNeighbor(right)) {
      return false
    }

    const ptL = edgePoints[left]
    const ptM = edgePoints[name]
    const ptR = edgePoints[right]

    // 3) Angle must be sharp enough
    const ang0 = angleDeg(ptL, ptM, ptR)
    if (ang0 === null || ang0 >= ANGLE_CANDIDATE) {
      return false
    }

    const lenMid = rayLenPx(edgePoints, centerXy, name)
    const lenLeft = rayLenPx(edgePoints, centerXy, left)
    const lenRight = rayLenPx(edgePoints, centerXy, right)

    if (lenMid === null || lenLeft === null || lenRight === null) {
      return false
    }

    const closeToLeft = withinPct(lenMid, lenLeft, 0.15)
    const closeToRight = withinPct(lenMid, lenRight, 0.15)

    if (!closeToLeft && !closeToRight) {
      return isInwardByCenter(axPxRounded, ayPxRounded, ptL, ptM, ptR, INWARD_MARGIN_PX)
    }

    const closeNeighbor =
      closeToLeft && closeToRight
        ? Math.abs(lenMid - lenLeft) <= Math.abs(lenMid - lenRight)
          ? left
          : right
        : closeToLeft
          ? left
          : right

    const medianLen = medianLenNonRunners()
    if (!isAbnormallyShortGlobal(closeNeighbor, 0.8, medianLen)) {
      return false
    }

    return isInwardByCenter(axPxRounded, ayPxRounded, ptL, ptM, ptR, INWARD_MARGIN_PX)
  }

  // Main Pass 4 loop: iterative dent fixing
  // CRITICAL FIX: Increased from 10 to 30 to match Python implementation
  const MAX_RESCAN_PASSES = 30
  const exhaustedRays = new Set<string>()

  for (let pass = 0; pass < MAX_RESCAN_PASSES; pass++) {
    if (!correctRayOutliers) {
      break
    }
    let changed = false

    for (const [name, v] of Object.entries(dirs)) {
      if (exhaustedRays.has(name)) {
        continue
      }
      if (!isCandidate(name)) {
        continue
      }

      // Get initial state
      const [ang0, left, right] = currentAngle(name)
      if (ang0 === null) {
        exhaustedRays.add(name)
        continue
      }

      // Try loosening parameters
      let curL: ParamLevel | null = baseLLevel
      let curAB: ParamLevel | null = baseABLevel

      // First move: loosen based on what stopped the original run
      const [hitL, hitAB] = classifyStopLAB(results[name])
      if (hitL) {
        const nxt = moveLevel(curL, 'looser')
        if (nxt) {
          curL = nxt
        }
      }
      if (hitAB) {
        const nxt = moveLevel(curAB, 'looser')
        if (nxt) {
          curAB = nxt
        }
      }

      // If we couldn't loosen anything, mark as exhausted
      if (curL === baseLLevel && curAB === baseABLevel) {
        exhaustedRays.add(name)
        continue
      }

      let accepted = false
      let acceptedRes: RayWalkResult | null = null
      let acceptedPt: [number, number] | null = null
      let acceptedAng: number | null = null
      let bestOutwardRes: RayWalkResult | null = null
      let bestOutwardPt: [number, number] | null = null
      let bestOutwardAng: number | null = null
      let bestImproveRes: RayWalkResult | null = null
      let bestImprovePt: [number, number] | null = null
      let bestImproveAng: number | null = null
      const medianLen = medianLenNonRunners()
      const leftShort = isAbnormallyShortGlobal(left, 0.8, medianLen)
      const rightShort = isAbnormallyShortGlobal(right, 0.8, medianLen)
      const hasShortNeighbor = leftShort || rightShort

      // Try up to 12 parameter combinations
      for (let attempt = 0; attempt < MAX_TRIES_PER_RAY; attempt++) {
        const paramsCur = buildAdjustedEdgeParams(curL!, curAB!)
        const resTry = runOneDirAdaptiveHelper(
          name,
          v,
          paramsCur,
          axPxRounded,
          ayPxRounded,
          radiusPx,
          samplingRadiusPx,
          patchHalfPxNormalized,
          stepPxNormalized,
          roiRgb,
          geoRef.W,
          geoRef.H,
          validMask,
          GREEN_AUTO_MAP_MIN_PIX,
          geoRef.mpp,
        )

        if (resTry?.lastGood) {
          const ptTry = resTry.lastGood.centerPx
          const angTry = angleDeg(edgePoints[left], ptTry, edgePoints[right])
          const inwardTry = isInwardByCenter(
            axPxRounded,
            ayPxRounded,
            edgePoints[left],
            ptTry,
            edgePoints[right],
            INWARD_MARGIN_PX,
          )

          // Accept if:
          const acceptByOutwardGood = angTry !== null && !inwardTry && angTry >= ANGLE_OUTWARD_GOOD_MIN
          const acceptBySharpOutwardDueToShortNeighbor =
            angTry !== null && !inwardTry && angTry < ANGLE_SHARP_OUTWARD_MAX && hasShortNeighbor
          const acceptByOutwardOk = angTry !== null && !inwardTry && angTry >= ANGLE_OUTWARD_OK_MIN
          const acceptByImprove = angTry !== null && angTry >= ang0 + IMPROVE_MIN_DEG

          if (acceptByOutwardOk && (bestOutwardAng === null || angTry > bestOutwardAng)) {
            bestOutwardRes = resTry
            bestOutwardPt = ptTry
            bestOutwardAng = angTry
          }

          if (acceptByImprove && (bestImproveAng === null || angTry > bestImproveAng)) {
            bestImproveRes = resTry
            bestImprovePt = ptTry
            bestImproveAng = angTry
          }

          if (acceptByOutwardGood || acceptBySharpOutwardDueToShortNeighbor) {
            acceptedRes = resTry
            acceptedPt = ptTry
            acceptedAng = angTry
            accepted = true
            break
          }
        }

        // Loosen further based on what stopped this try
        const [hL, hAB] = classifyStopLAB(resTry)
        let nextL = hL && curL ? moveLevel(curL, 'looser') : curL
        let nextAB = hAB && curAB ? moveLevel(curAB, 'looser') : curAB
        if (!nextL) {
          nextL = curL
        }
        if (!nextAB) {
          nextAB = curAB
        }

        // No progress -> stop trying
        if (nextL === curL && nextAB === curAB) {
          break
        }

        curL = nextL
        curAB = nextAB
      }

      if (!accepted) {
        if (bestOutwardRes !== null && bestOutwardPt !== null) {
          acceptedRes = bestOutwardRes
          acceptedPt = bestOutwardPt
          acceptedAng = bestOutwardAng
          accepted = true
        } else if (bestImproveRes !== null && bestImprovePt !== null) {
          acceptedRes = bestImproveRes
          acceptedPt = bestImprovePt
          acceptedAng = bestImproveAng
          accepted = true
        }
      }

      if (accepted && acceptedRes !== null && acceptedPt !== null) {
        results[name] = acceptedRes
        edgePoints[name] = acceptedPt
        changed = true
        inwardFixLog[name] = inwardFixLog[name] || { history: [] }
        inwardFixLog[name].history.push({
          pass: 4,
          angleBefore: ang0,
          angleAfter: acceptedAng,
          leftShort,
          rightShort,
        })
      }

      if (!accepted) {
        exhaustedRays.add(name)
      }

      // Fix one dent per pass, then restart scan
      if (changed) {
        break
      }
    }

    // If no changes in this pass, we're done
    if (!changed) {
      break
    }
  }

  // ======== PASS 5: Final runner fix after Pass 4 ========

  for (const [name, v] of Object.entries(dirs)) {
    if (!correctRayOutliers) {
      break
    }
    if (!shouldRetryRunner(name)) {
      continue
    }

    // Re-run Pass 2 logic with stricter parameters
    let curLLevel: ParamLevel | null = baseLLevel
    let curABLevel: ParamLevel | null = baseABLevel

    const [left, right] = getNeighborNames(name)
    if (!edgePoints[left] || !edgePoints[right]) {
      continue
    }

    const origPt = edgePoints[name]
    if (!origPt) {
      continue
    }

    const origAng = angleDeg(edgePoints[left], origPt, edgePoints[right])
    if (origAng === null) {
      continue
    }

    let bestRes = results[name]
    let bestPt = origPt
    let bestAng = origAng

    // Try up to 6 stricter parameter levels
    for (let attempt = 0; attempt < 6; attempt++) {
      const nextL = curLLevel ? moveLevel(curLLevel, 'stricter') : null
      const nextAB = curABLevel ? moveLevel(curABLevel, 'stricter') : null

      if (!nextL && !nextAB) {
        break
      }

      if (nextL) {
        curLLevel = nextL
      }
      if (nextAB) {
        curABLevel = nextAB
      }

      const paramsStricter = buildAdjustedEdgeParams(curLLevel!, curABLevel!)
      const resTry = runOneDirAdaptiveHelper(
        name,
        v,
        paramsStricter,
        axPxRounded,
        ayPxRounded,
        radiusPx,
        samplingRadiusPx,
        patchHalfPxNormalized,
        stepPxNormalized,
        roiRgb,
        geoRef.W,
        geoRef.H,
        validMask,
        GREEN_AUTO_MAP_MIN_PIX,
        geoRef.mpp,
      )

      if (!resTry || !resTry.lastGood) {
        continue
      }

      const ptTry = resTry.lastGood.centerPx
      const angTry = angleDeg(edgePoints[left], ptTry, edgePoints[right])

      if (angTry === null) {
        continue
      }

      // Keep if angle improved
      if (angTry > bestAng) {
        bestAng = angTry
        bestRes = resTry
        bestPt = ptTry
      }

      // Early stop if outward and good angle
      const inwardTry = isInwardByCenter(
        axPxRounded,
        ayPxRounded,
        edgePoints[left],
        ptTry,
        edgePoints[right],
        INWARD_MARGIN_PX,
      )
      if (!inwardTry && angTry >= 150.0) {
        break
      }
    }

    // // Accept if improved
    if (bestAng > origAng) {
      results[name] = bestRes
      edgePoints[name] = bestPt
      runnerFixLog[name] = runnerFixLog[name] || []
      runnerFixLog[name].push({ pass: 5, improved: true, levels: { L: curLLevel, AB: curABLevel } })
    }
  }

  if (debugStepLogging) {
    logGreenAutoMapStepDetails({ results, chosenPreset })
  }

  if (debugStopLogging) {
    logGreenAutoMapStopDiagnostics({
      results,
      chosenPreset,
      baseLevels: { L: baseLLevel, AB: baseABLevel },
      edgeParams,
      mpp: geoRef.mpp,
      probeSelectionDebug,
      runnerFixLog,
      lengthSpikeFixLog,
      inwardFixLog,
    })
  }

  if (debugImageLogging) {
    const sampleResult = Object.values(results).find((result): result is RayWalkResult => Boolean(result)) ?? null

    logGreenAutoMapScaleInfo({
      geoRef,
      roiRgb,
      centerPx: centerXy,
      probeRadiusM,
      patchHalfPx,
      maxSearchRadiusM,
      radiusPx,
      sampleResult,
    })

    logGreenAutoMapOverlayPreview({
      roiRgb,
      centerPx: centerXy,
      dirs,
      results,
      probeResult,
      radiusPx,
      chosenPreset,
    })
  }

  return {
    geoRef,
    rayCount: totalDirs,
    centerPx: [axPxRounded, ayPxRounded],
    edgePoints: Object.fromEntries(
      Object.entries(edgePoints).map(([dir, px]) => {
        if (!px) {
          return [dir, null]
        }
        const [lat, lng] = roiPxToLatLon(px[0], px[1], geoRef)
        return [dir, { lat, lng }]
      }),
    ),
    results,
    probeResult: probeResult,
    edgeParams: edgeParams,
    chosenPreset,
    baseLevels: { L: baseLLevel, AB: baseABLevel },
    logs: {
      runnerFixes: runnerFixLog,
      lengthSpikeFixes: lengthSpikeFixLog,
      inwardFixes: inwardFixLog,
    },
  }
}
